import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

export default function Hero() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <section id="home" className="relative min-h-[100dvh] bg-ayur-950 flex items-center justify-center overflow-hidden">

      <div className="absolute inset-0 z-0">

        {/* Load only the image needed for the current viewport. */}
        <picture className="absolute inset-0 block">
          <source media="(max-width: 639px)" srcSet="/images/mobile.png" />
          <motion.img
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 3, ease: "easeOut" }}
            src="/images/mam.jpg"
            alt="VASEEGRAH VEDA Wellness"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-center"
          />
        </picture>

        <div className="absolute inset-0 bg-gradient-to-t from-ayur-950/90 via-ayur-950/50 to-ayur-950/30 sm:from-ayur-950/80 sm:via-transparent sm:to-ayur-950/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-ayur-950/80 via-ayur-950/40 to-transparent sm:from-ayur-950/60 sm:via-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(252,176,66,0.15)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.05)_0%,transparent_50%)]" />
      </div>

      <div className="relative z-10 max-w-[90rem] mx-auto px-5 sm:px-6 lg:px-16 w-full pt-28 sm:pt-32 pb-16 sm:pb-20">
        <div className="max-w-4xl">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{
              show: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } }
            }}
            className="flex flex-col items-start"
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-3 sm:gap-4 mb-7 sm:mb-10">
              <div className="h-px w-10 sm:w-16 bg-ayur-400 shrink-0" />
              <span className="text-[11px] sm:text-[13px] font-sans font-black tracking-[0.35em] sm:tracking-[0.4em] uppercase text-white drop-shadow-md">
                The Essence of Purity
              </span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-5xl sm:text-6xl md:text-8xl lg:text-[7.8rem] font-serif font-bold text-white leading-[0.95] tracking-tight mb-7 sm:mb-10 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)]"
            >
              Pure Ayurveda. <br />
              <span className="opacity-95">Elevated.</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-base sm:text-xl md:text-2xl font-sans font-medium leading-relaxed text-white/90 max-w-xs sm:max-w-xl md:max-w-2xl mb-10 sm:mb-14 drop-shadow-sm"
            >
              Discover profound healing through our deeply researched, 100% natural herbal formulations. Rooted in tradition, refined for modern luxury.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-8 w-full sm:w-auto"
            >
              <a
                href="#ecosystem"
                className="group relative inline-flex items-center justify-center px-8 sm:px-10 py-4 sm:py-5 bg-ayur-400 text-white font-sans font-bold text-xs uppercase tracking-[0.25em] transition-all duration-500 hover:bg-ayur-500 hover:shadow-luxury-hover hover:-translate-y-1 w-full sm:w-auto overflow-hidden"
              >
                <span className="relative z-10">Explore Collection</span>
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              </a>

              <a
                href="#philosophy"
                className="group inline-flex items-center justify-center gap-3 sm:gap-4 px-8 sm:px-10 py-4 sm:py-5 bg-transparent border border-white/30 text-white font-sans font-bold text-xs uppercase tracking-[0.25em] transition-all duration-500 hover:border-white hover:bg-white/10 w-full sm:w-auto"
              >
                Our Philosophy <ArrowRight size={16} className="transition-transform group-hover:translate-x-1.5" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="absolute right-0 bottom-1/4 w-px h-32 bg-gradient-to-b from-transparent via-ayur-400/50 to-transparent hidden lg:block" />
    </section>
  );
}
